import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  CheckCircle2,
  Calendar,
  Bot,
  User,
  CreditCard,
  ChevronRight,
  Lock,
  Clock,
  FileText,
  Video
} from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServiceCompletedDashboard = ({ caseData, user }) => {
  const navigate = useNavigate();
  const [purchasingSimulation, setPurchasingSimulation] = useState(false);

  const daysUntilInterview = () => {
    if (!caseData?.interview_date) return null;
    const today = new Date();
    const interview = new Date(caseData.interview_date);
    const diffTime = interview - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handlePurchaseSimulation = async () => {
    setPurchasingSimulation(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        product_id: "simulacro_humano",
        quantity: 1
      });
      
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("Error al crear la sesión de pago");
      }
    } catch (error) {
      console.error("Error purchasing simulation:", error);
      toast.error("Error al procesar el pago. Intenta de nuevo.");
    } finally {
      setPurchasingSimulation(false);
    }
  };

  const days = daysUntilInterview();

  return (
    <div className="space-y-6">
      {/* Service Completed Banner */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white"
        data-testid="service-completed-banner"
      >
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">¡Servicio Completado!</h2>
            <p className="text-emerald-100">Tu proceso DS-160 y pago MRV han sido finalizados</p>
          </div>
        </div>
        
        {/* MRV Info */}
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur rounded-lg p-4">
            <p className="text-emerald-200 text-sm mb-1">Número MRV</p>
            <p className="font-bold text-xl">{caseData.mrv_number || "N/A"}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-4">
            <p className="text-emerald-200 text-sm mb-1">Fecha de Entrevista</p>
            <p className="font-bold text-xl">{caseData.interview_date || "Por confirmar"}</p>
          </div>
        </div>
      </motion.div>

      {/* Countdown to Interview */}
      {days !== null && days > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-amber-500 rounded-xl flex items-center justify-center">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-amber-800 font-medium">Tu entrevista es en</p>
                <p className="text-4xl font-bold text-amber-900">{days} días</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-amber-700">¡Prepárate con el simulador!</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Available Services */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-navy-100 p-6"
      >
        <h3 className="font-heading text-xl font-bold text-navy-900 mb-6">
          Servicios Disponibles
        </h3>
        
        <div className="grid sm:grid-cols-2 gap-4">
          {/* AI Simulator - Free */}
          <div 
            className="p-5 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:border-emerald-400 transition-all cursor-pointer"
            onClick={() => navigate("/interview-simulator")}
            data-testid="ai-simulator-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">
                GRATIS
              </span>
            </div>
            <h4 className="font-bold text-navy-900 text-lg mb-1">Simulador IA</h4>
            <p className="text-sm text-navy-600 mb-3">
              Practica con nuestro simulador de entrevista consular con IA. Ilimitado.
            </p>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
              Practicar Ahora
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Human Simulation - $35 */}
          <div 
            className="p-5 rounded-xl border-2 border-purple-200 bg-purple-50 hover:border-purple-400 transition-all"
            data-testid="human-simulation-card"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <span className="px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                $35 USD
              </span>
            </div>
            <h4 className="font-bold text-navy-900 text-lg mb-1">Simulacro con Oficial</h4>
            <p className="text-sm text-navy-600 mb-3">
              Sesión de 30 minutos vía Zoom con un oficial experto. Feedback personalizado.
            </p>
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={handlePurchaseSimulation}
              disabled={purchasingSimulation}
            >
              {purchasingSimulation ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Comprar Sesión
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Disabled Features Notice */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-navy-50 rounded-xl border border-navy-200 p-6"
      >
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-navy-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-navy-700 mb-2">Funciones Completadas</h4>
            <p className="text-sm text-navy-600">
              Las siguientes funciones ya no están disponibles porque tu servicio principal ha sido completado:
            </p>
            <ul className="mt-3 space-y-2 text-sm text-navy-500">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Edición del DS-160 (ya enviado)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Subida de documentos (proceso completo)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Chat gratuito con oficial (servicio cerrado)
              </li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* View Documents */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl border border-navy-100 p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-navy-900">Tu Expediente</h4>
              <p className="text-sm text-navy-500">Revisa tus documentos y datos del DS-160</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate("/documentos")}
          >
            Ver Documentos
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default ServiceCompletedDashboard;
